import { Observable } from 'rxjs/Observable'
import { combineEpics } from 'redux-observable'
import { captureException } from 'utils/SentryHelpers'
import { replace, LOCATION_CHANGE } from 'react-router-redux'
import { oxford } from 'humanize-plus'
import { is } from 'immutable'

import app from 'containers/app'
import { api, demoApi } from './api'
import { CONVERSATIONS_REGEX } from './constants'
import { SortConversationsEnum } from 'constants/SortConversationsConstants'
import { conversationIdFromPathname } from './helpers'
import * as actions from './actions'
import * as selectors from './selectors'
import * as EndUserActions from 'app/modules/endUser/actions'
import { receiveEndUserCompanyData, fetchBulkConversationEnrichmentDataSuccess } from 'app/modules/enrichment/actions'
import * as RevealConstants from 'app/modules/reveal/constants'
import * as SocketConstants from 'app/modules/socket/constants'
import * as UserSelectors from 'app/modules/user/selectors'
import { hasConversationAccess } from 'app/modules/access/helpers'
import { parseConversation } from 'app/modules/conversation/models'
import * as InboxSelectors from 'app/modules/inbox/selectors'
import { hasCompactViewEnabled } from 'app/modules/user/properties/selectors'
import * as InboxActions from 'app/modules/inbox/actions'
import * as PlaybookActions from 'app/modules/playbook/actions'
import * as OrgActions from 'app/modules/org/actions'
import * as BulkUpdateConversationActions from './bulkUpdate/actions'
import * as GateSelectors from 'app/modules/gates/selectors'
import * as DemoConstants from './demodata'
import { clearAll } from '../../cache/filters'
import { SHOW_ABM_MODAL } from 'app/modules/notification/constants'
import { api as endUserApi } from 'app/modules/endUser/api'
import { track } from 'app/modules/tracking/actions'

export function conversationChanged(action$) {
  return action$
    .ofType(LOCATION_CHANGE)
    .pluck('payload')
    .filter(({ pathname }) => CONVERSATIONS_REGEX.test(pathname))
    .map(({ pathname }) => conversationIdFromPathname(pathname))
    .map(conversationId => actions.conversationChanged({ conversationId }))
}

const INVALID_TIMESTAMP = 9223372036854776000

function fetchConversation(action$, { getState }) {
  return action$
    .ofType(actions.fetchConversation.toString())
    .map(action => action.payload)
    .filter(({ conversationId, force }) => {
      const conversationLoaded = selectors.makeConversationLoadedSelector()
      return !conversationLoaded(getState(), { conversationId }) || force
    })
    .mergeMap(({ conversationId }) => {

      if (GateSelectors.hasDemoOrg(getState())) {
        const paginatedConversationDataSelector = selectors.makePaginatedConversationData()
        const paginatedConversationData = paginatedConversationDataSelector(getState(), { conversationId })

        const conversation = parseConversation({
          id: conversationId,
          ...DemoConstants.conversation({ conversationId, endUserId: paginatedConversationData && paginatedConversationData.endUserId })
        })
        return Observable.of(actions.receiveConversation({ conversation }))
      } else {
        return api.fetch(conversationId)
        .map((conversation) => {
          // NOTE: Some conversations mistakenly had `lastSentMessageTimestamp`
          // set to INVALID_TIMESTAMP. This logic overwrites that value to
          // `undefined` so that the invalid value is ignored in reducer logic.
          let validConversation = conversation

          if (conversation.get('lastSentMessageTimestamp') === INVALID_TIMESTAMP) {
            validConversation = validConversation.set('lastSentMessageTimestamp', undefined)
          }

          return validConversation
        })
        .map((conversation) => actions.receiveConversation({ conversation }))
        .startWith(actions.fetchConversationPending({ conversationId }))
        .catch(error => {
          return Observable.of(actions.fetchConversationFailed({ conversationId, error }))
        })
      }
    })
}

function getInfluencingPlaybookForConversation(action$) {
  return action$
    .ofType(actions.receiveConversation.toString())
    .pluck('payload')
    .filter(({ conversation }) => !!conversation.getIn(['context', 'influencingCampaignId'], 0))
    .mergeMap(({ conversation }) => {
      const influencingCampaignId = conversation.getIn(['context', 'influencingCampaignId'], 0)
      return Observable.of(PlaybookActions.getPlaybookByCampaignId({ campaignId: influencingCampaignId }))
    })
}

const extractQueryParams = ({ query }) => {
  return {
    limit: 25,
    status: query.get('statusFilter'),
    tag: query.get('conversationTagFilter'),
    participantId: query.get('participantFilter'),
    sortBy: query.get('sortBy'),
    inboxId: query.get('inboxId'),
  }
}

const fetchConversationsByInbox = (action$, { getState }) =>
  action$
    .ofType(actions.setQuery.toString())
    .map(() => actions.fetchConversationList({ query: selectors.activeQuery(getState()) }))

const fetchConversationListClassic = ({ getState }) => ({ query, queryParams }) => {
  const fetchConversations$ = (GateSelectors.hasDemoOrg(getState()) ? demoApi : api)
    .fetchConversationsByInbox(queryParams)

  return fetchConversations$
    .map(paginatedConversations => actions.fetchConversationListSuccess({ query, paginatedConversations }))
}

// Using the new expanded ConversationClientAPI, we now short-circuit the need
// for additional round-trips to fetch endUsers and enrichmentData. Since we
// have access to multiple collections in a single response, we dispatch
// fetchSuccess actions for the respective collections.
const fetchConversationListExpanded = ({ getState }) => ({ query, queryParams }) => {
  const fetchEnrichedConversations$ = (GateSelectors.hasDemoOrg(getState()) ? demoApi : api)
    .fetchEnrichedConversationsByInbox(queryParams)

  return fetchEnrichedConversations$
    .mergeMap(({
      paginatedConversations,
      endUsers,
      enrichedCompanyDataByEndUserId,
    }) => {
      return Observable.of(
        actions.fetchConversationListSuccess({ query, paginatedConversations }),
        EndUserActions.fetchEndUserListSuccess({ ids: endUsers.map(endUser => endUser.id).toArray(), data: endUsers }),
        ...enrichedCompanyDataByEndUserId
          .entrySeq()
          .toArray()
          .map(([endUserId, data]) => receiveEndUserCompanyData({ endUserId, data })),
      )
    })
}

const fetchBulkConversationEnrichmentData = (action$, store) => {
  return action$
    .ofType(actions.fetchConversationListSuccess.toString())
    .pluck('payload')
    .filter(() => hasCompactViewEnabled(store.getState()))
    .mergeMap(({ paginatedConversations }) => {
      let convoEndUser = paginatedConversations.get('data').map((c) => {
        return { endUserId: c.endUserId, conversationId: c.id }
      }).toArray()
      return api.fetchBulkConversationEnrichmentData(convoEndUser).map((data) => fetchBulkConversationEnrichmentDataSuccess({ data }))
    }).catch((error) => {
      captureException(error)
      return Observable.empty()
    })
}

// Abstract away fetching conversations via the classic or expanded APIs.
// Fires off a PENDING action and gracefully handles errors by cleaning the
// filter cache and redirecting to the inboxes root route.
const fetchConversationList = (action$, store) =>
  action$
    .ofType(actions.fetchConversationList.toString())
    .pluck('payload')
    .mergeMap(({ query, queryParams = extractQueryParams({ query }) }) => {
      const fetchConversationList$ = fetchConversationListClassic(store)({ query, queryParams })

      return fetchConversationList$
        .startWith(actions.fetchConversationListPending({ query }))
        .catch(error => {
          if (error.status > 400 && error.status < 500) {
            clearAll()
            return Observable.of(replace({ pathname: '/inboxes' }))
          }
          return Observable.of(actions.fetchConversationListFailed({ query, error }))
        })
    })

// Fire off an action to fetch the next page of results by hooking into the
// existing `fetchConversationsByInbox` epic, threading through an additional
// query param to represent the pagination offset.
const fetchNextConversationsByInbox = (action$, { getState }) =>
  action$
    .ofType(actions.fetchNextConversationList.toString())
    .pluck('payload')
    .mergeMap((payload = { query: selectors.activeQuery(getState()) }) => {
      const { query } = payload
      const queryParams = extractQueryParams({ query })
      const cursorQueryParam = (queryParams.sortBy === SortConversationsEnum.OLDEST.value)
       ? 'before'
       : 'after'

      const fetchNextFailed$ = action$
        .ofType(actions.fetchConversationListFailed.toString())
        .pluck('payload')
        .filter(payload => is(query, payload.query))
        .first()

      const fetchNextSuccess$ = action$
       .ofType(actions.fetchConversationListSuccess.toString())
       .pluck('payload')
       .filter(payload => is(query, payload.query))
       .first()
       .map(actions.fetchNextConversationListSuccess)

      return Observable
        .race(
          fetchNextFailed$,
          fetchNextSuccess$
        )
        .startWith(
          actions.fetchConversationList({ query, queryParams: {
            ...queryParams,
            [cursorQueryParam]: selectors.nextCursor(getState(), { query }),
          }})
        )
    })

function fetchEndUsersForConversationsList(action$, { getState }) {
  return action$
    .ofType(
      actions.fetchConversationListSuccess.toString(),
      actions.fetchNextConversationListSuccess.toString()
    )
    .pluck('payload', 'paginatedConversations', 'data')
    .mergeMap(conversations => GateSelectors.hasSplitEndUserChannel(getState())
      ? Observable.of(EndUserActions.fetchEndUserList({ ids: conversations.map(({ endUserId }) => endUserId).toArray() })) // TODO: Remove after socket auth fixed
      : Observable.of(EndUserActions.fetchEndUserList({ ids: conversations.map(({ endUserId }) => endUserId).toArray() })))
}

function fetchConversationsCountForEndUserId(action$) {
  return action$
    .ofType(actions.fetchConversationsCountForEndUserId.toString())
    .pluck('payload')
    .mergeMap(({ id }) =>
      api.fetchConversationsCountForEndUserId(id)
        .map(count => {
          return actions.fetchConversationsCountForEndUserIdSuccess({ id, count })
        })
        .startWith(actions.fetchConversationsCountForEndUserIdPending())
        .catch(error => Observable.of(actions.fetchConversationsCountForEndUserIdFailed(error)))
    )
}

function patchConversation(action$) {
  return action$
    .ofType(actions.patchConversation.toString())
    .pluck('payload')
    .mergeMap(({ id, update }) => {
      return api
        .patch(id, update)
        .map(conversation => actions.receiveConversation({ conversation }))
        .startWith(actions.patchConversationPending({ id }))
        .catch(error => {
          captureException(error)
          return Observable.of(actions.patchConversationFailed({ id }))
        })
    })
}

function createConversation(action$) {
  return action$
    .ofType(actions.createConversation.toString())
    .map(action => action.payload)
    .mergeMap(({ message }) => api
      .create(message)
      .map(conversation => actions.receiveConversation({ conversation }))
      .startWith(actions.createConversationPending())
      .catch(error => {
        return Observable.of(actions.createConversationFailed({ error }))
      })
    )
}

function fetchCurrentConversation(action$) {
  return action$.ofType(actions.conversationChanged.toString())
    .pluck('payload', 'conversationId')
    .filter(conversationId => !!conversationId)
    .map(conversationId => actions.fetchConversation({ conversationId }))
}

function fetchConversationEndUser(action$, { getState }) {
  return action$
    .ofType(actions.receiveConversation.toString())
    .mergeMap(action =>
      GateSelectors.hasSplitEndUserChannel(getState())
        ? Observable.of(EndUserActions.fetchEndUser({ id: action.payload.conversation.endUserId })) // TODO: Remove after socket auth fixed
        : Observable.of(EndUserActions.fetchEndUser({ id: action.payload.conversation.endUserId }))

    )
}

function sendConversationTranscriptEpic(action$) {
  return action$
    .ofType(actions.sendConversationTranscript.toString())
    .pluck('payload')
    .mergeMap((payload) => {
      const { emails = [], conversationId } = payload
      const requests = emails.map((email) => api.sendConversationTranscript({ conversationId, email }))
      return Observable.forkJoin(requests)
        .map(() => actions.sendConversationTranscriptSuccess(payload))
        .catch(error => Observable.of(actions.sendConversationTranscriptFailure({ ...payload, error })))
    })
}

function sendConversationTranscriptSuccessNotifyEpic(action$) {
  return action$
    .ofType(actions.sendConversationTranscriptSuccess.toString())
    .pluck('payload')
    .filter(({ emails = []}) => emails.length > 0)
    .do(({ emails }) => {
      app.actions.NotificationActions.pushNotification({
        content: `This chat transcript was successfully sent to ${oxford(emails, 4)}!`,
        variant: "green",
      })
    })
    .mergeMap(() => Observable.empty())
}

function sendConversationTranscriptFailureNotifyEpic(action$) {
  return action$
    .ofType(actions.sendConversationTranscriptFailure.toString())
    .do(() => {
      app.actions.NotificationActions.pushNotification({
        content: "We were unable to send a transcript for this conversation.",
        variant: "red",
      })
    })
    .mergeMap(() => Observable.empty())
}

function pushConversation(action$, store) {
  return action$
    .ofType(SocketConstants.JOIN_CHANNEL_PENDING)
    .pluck('payload')
    .filter((payload) => payload.clusterName === SocketConstants.SocketCluster.MESSAGES)
    .filter((payload) => payload.channelName === SocketConstants.SocketChannel.ORG_PRIVATE)
    .switchMap(({ channel, leaveChannel$ }) => {
      return Observable.create((observer) => {
        const conversationDispatchHandler = (currentUser, conversation) => {
          if (conversation.status === 'BULK_SENT') {
            return
          }
          if (hasConversationAccess(conversation.participantIds, currentUser)) {
            observer.next(actions.receiveConversation({
              inboxId: conversation.inboxId,
              conversation: conversation
            }))
          }
        }

        channel.on('conversation-new', (payload) => {
          const conversation = parseConversation(payload)
          const currentUser = UserSelectors.currentUser(store.getState())
          const currentInboxId = InboxSelectors.currentInboxId(store.getState())

          conversationDispatchHandler(currentUser, conversation)

          if (conversation.inboxId === currentInboxId && hasConversationAccess(conversation.participantIds, currentUser)) {
            observer.next(InboxActions.changeStatus({
              id: currentInboxId,
              status: conversation.status,
              currentParticipants: conversation.participantIds,
              currentUserId: currentUser.id
            }))
          }
        })

        channel.on('conversation-updated', (payload) => {
          const conversation = parseConversation(payload)
          const currentUser = UserSelectors.currentUser(store.getState())
          conversationDispatchHandler(currentUser, conversation)
        })
      })
        .takeUntil(leaveChannel$)
    })
}

const setContactTabIndexOnShowRevealData = (action$, store) =>
  action$
    .ofType(RevealConstants.SHOW_REVEAL_DATA)
    .map(() => actions.setActiveConversationContactTabIndex(1))


const fetchRoutingAuditLog = (action$, store) => {
  return action$
    .ofType(actions.fetchRoutingAuditLog.toString())
    .pluck('payload')
    .filter(({ routingGroupId }) => selectors.getRoutingAuditLogNeverLoaded(store.getState(), routingGroupId))
    .mergeMap(({ conversationId, routingGroupId }) => (
      api
        .fetchRoutingAuditLog({ conversationId, routingGroupId })
        .map((data) => actions.fetchRoutingAuditLogSuccess({ data, routingGroupId }))
        .startWith(actions.fetchRoutingAuditLogPending({ routingGroupId }))
        .catch((err) => Observable.of(actions.fetchRoutingAuditLogFailed({ err, routingGroupId })))
    ))
}

const deselectAllConversationsOnReceiveBulkUpdate = action$ =>
  action$
    .ofType(BulkUpdateConversationActions.receiveBulkUpdateSuccess.toString())
    .pluck('payload')
    .map(({ conversationDiffs }) => {
      const conversationIds = conversationDiffs.map(conversationDiff => conversationDiff.conversationId)

      return actions.deselectConversations({ conversationIds })
    })

const updateConversationsOnReceiveBulkUpdateSuccess = (action$) =>
  action$
    .ofType(BulkUpdateConversationActions.receiveBulkUpdateSuccess.toString())
    .pluck('payload')
    .map(({ conversationDiffs }) => {
      const updates = conversationDiffs
        .map(({
          conversationId,
          currentStatus,
          currentParticipantIds
        }) => {
          return {
            conversationId,
            conversationStatus: currentStatus,
            participantIds: currentParticipantIds
          }
        })

      return actions.updateConversations({ updates })
    })

const setConversationLimit = (action$) =>
  action$.ofType(actions.setConversationLimit.toString())
  .pluck('payload')
  .mergeMap(({ limitNumChatsEnabled, limitNumChats }) => {
    return api.setConversationLimit(limitNumChatsEnabled, limitNumChats)
      .switchMap((result) => {
        return Observable.create(observer => {
          observer.next(actions.setConversationLimitSuccess({ limitNumChatsEnabled, limitNumChats }))
          observer.next(OrgActions.receiveOrgProperties(result.response))
        })
      })
        .startWith(actions.setConversationLimitPending({ limitNumChatsEnabled, limitNumChats }))
        .catch((err) => Observable.of(actions.setConversationLimitFailure({ err, limitNumChatsEnabled, limitNumChats })))
  })

const fetchGlobalAutoCloseConfiguration = (action$) =>
  action$.ofType(actions.fetchGlobalAutoCloseConfiguration.toString())
  .pluck('payload')
  .mergeMap(() => {
    return api.fetchGlobalAutoCloseConfiguration()
      .map(configuration => actions.fetchGlobalAutoCloseConfigurationSuccess(configuration))
      .startWith(actions.fetchGlobalAutoCloseConfigurationPending())
      .catch((error) => Observable.of(actions.fetchGlobalAutoCloseConfigurationFailure(error)))
  })

const updateGlobalAutoCloseConfiguration = (action$) =>
  action$.ofType(actions.updateGlobalAutoCloseConfiguration.toString())
  .pluck('payload')
  .mergeMap(configuration => {
    return api.updateGlobalAutoCloseConfiguration(configuration)
      .map(() => actions.updateGlobalAutoCloseConfigurationSuccess(configuration))
      .startWith(actions.updateGlobalAutoCloseConfigurationPending())
      .catch((error) => Observable.of(actions.updateGlobalAutoCloseConfigurationFailure(error)))
  })

const showComposeModalOnAbmNotificationClick = action$ => action$
  .ofType(SHOW_ABM_MODAL)
  .pluck('payload')
  .mergeMap(endUserIdAndAttributes =>  {
    const endUser$ = endUserApi.fetch(endUserIdAndAttributes.endUserId)
    return endUser$.map(endUser => ({endUser, companyName: endUserIdAndAttributes.companyName, page:endUserIdAndAttributes.page }))
  })
  .map(endUserAndAttributes => {
    return actions.setComposeModalStatus({
      status: true,
      endUser: endUserAndAttributes.endUser,
      composeType: "ABM",
      companyName: endUserAndAttributes.companyName,
      page: endUserAndAttributes.page
    })
  })

const fetchSuggestedArticles = action$ =>
  action$.ofType(actions.fetchSuggestedArticles.toString())
    .pluck('payload')
    .switchMap(payload => {
      return api.fetchSuggestedArticles(payload.lastMessageText)
        .map(response => {
          if (response.suggestedActions.length > 0) {
            const { conversationId } = payload
            track('Got suggested articles', { conversationId })
          }
          return actions.fetchSuggestedArticlesSuccess(response)
        })
        .startWith(() => actions.fetchSuggestedArticlesPending())
        .catch(err => {
          return Observable.of(actions.fetchSuggestedArticlesFailed(err))
        })
    })


export const epic = combineEpics(
  fetchConversation,
  fetchConversationList,
  getInfluencingPlaybookForConversation,
  fetchConversationsByInbox,
  fetchNextConversationsByInbox,
  fetchEndUsersForConversationsList,
  fetchCurrentConversation,
  createConversation,
  fetchConversationEndUser,
  fetchConversationsCountForEndUserId,
  conversationChanged,
  sendConversationTranscriptEpic,
  sendConversationTranscriptSuccessNotifyEpic,
  sendConversationTranscriptFailureNotifyEpic,
  patchConversation,
  pushConversation,
  setContactTabIndexOnShowRevealData,
  fetchRoutingAuditLog,
  deselectAllConversationsOnReceiveBulkUpdate,
  updateConversationsOnReceiveBulkUpdateSuccess,
  setConversationLimit,
  showComposeModalOnAbmNotificationClick,
  fetchSuggestedArticles,
  fetchBulkConversationEnrichmentData,
  fetchGlobalAutoCloseConfiguration,
  updateGlobalAutoCloseConfiguration,
)



// WEBPACK FOOTER //
// ./src/scripts/app/modules/conversation/epic.js